import {executeAiAgentProposal} from '../../../redux/aiAgent/aiAgentActions';
import type {ExecuteAiAgentProposalParams} from '../../../redux/aiAgent/aiAgentActions';
import type {AiAgentExecuteResult} from '../../../redux/aiAgent/aiAgent.type';

export type ExecuteProposalOutcome = {
    result: AiAgentExecuteResult | null;
    /** The server's own wording for an action it decided must not happen. */
    refusal: string | null;
};

type RequestError = {response?: {status?: number; data?: {errorMessage?: string}}};

/** The one status the server refuses with: it answers with a reason the producer can act on. */
const REFUSED = 409;

/**
 * A refusal is the server deciding the action must not happen — the event is already
 * published, so it is edited in its own form. Its wording is the answer to the producer, and
 * pressing again can only repeat it, so the summary is not held for a second attempt.
 *
 * Only 409 qualifies. The 400 (aiAgentExecuteFailed) and the 403 both answer with a generic
 * "something went wrong, try again" — an execution that failed, not a decision — and taking
 * away the confirm button there would make the producer dictate every value again.
 */
function getRefusal(error: unknown): string | null {
    const {status, data} = (error as RequestError)?.response ?? {};
    if(status !== REFUSED) {
        return null;
    }
    return data?.errorMessage?.trim() || null;
}

/**
 * The request runs with handleError, so a rejection carries the server's response instead of
 * a toast — this is the only place that can tell a refusal from a failure.
 */
export async function executeProposalOutcome(
    params: ExecuteAiAgentProposalParams,
): Promise<ExecuteProposalOutcome> {
    try {
        return {result: await executeAiAgentProposal(params), refusal: null};
    } catch (error) {
        return {result: null, refusal: getRefusal(error)};
    }
}
